import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Button,
  Spinner,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import { Warning24Filled } from '@fluentui/react-icons';
import { BaseDialog } from './BaseDialog';
import CompanyPicker from './CompanyPicker';
import {
  fetchCustomerContacts,
  setCustomerCompany,
  deleteCustomer,
  type Customer,
} from '../services/customerService';

interface CompanyDeleteConflictModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** TWM customers.id of the Company that failed to delete. */
  companyId: string;
  /** Display name of the Company (best effort — fetched from list cache). */
  companyName: string;
  /** Called after the Company is successfully archived (via either path). */
  onResolved: () => void;
  /** Called whenever a contact is reassigned or the company is deleted, so
   *  the parent can refresh caches between steps. */
  onMutated?: () => void;
}

type Mode = 'choose' | 'reassign' | 'cascadeConfirm';

const useStyles = makeStyles({
  body: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('16px'),
  },
  warningRow: {
    display: 'flex',
    alignItems: 'flex-start',
    ...shorthands.gap('12px'),
    ...shorthands.padding('12px'),
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.border('1px', 'solid', tokens.colorPalettePumpkinBorderActive),
    ...shorthands.borderRadius('4px'),
  },
  warningIcon: {
    color: tokens.colorPalettePumpkinBorderActive,
    flexShrink: 0,
  },
  contactList: {
    display: 'flex',
    flexDirection: 'column',
    maxHeight: '240px',
    overflowY: 'auto',
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
    ...shorthands.borderRadius('4px'),
  },
  contactRow: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.padding('8px', '12px'),
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke2),
    ':last-child': {
      borderBottomWidth: 0,
    },
  },
  contactName: {
    fontWeight: tokens.fontWeightSemibold,
    fontSize: '14px',
  },
  contactEmail: {
    color: tokens.colorNeutralForeground3,
    fontSize: '12px',
  },
  pickerLabel: {
    fontWeight: tokens.fontWeightSemibold,
    fontSize: '14px',
    marginBottom: '4px',
  },
  errorBanner: {
    color: tokens.colorPaletteRedForeground1,
    fontSize: '13px',
  },
  dangerCascade: {
    color: tokens.colorPaletteRedForeground1,
    fontWeight: tokens.fontWeightSemibold,
  },
});

const CompanyDeleteConflictModal: React.FC<CompanyDeleteConflictModalProps> = ({
  isOpen,
  onClose,
  companyId,
  companyName,
  onResolved,
  onMutated,
}) => {
  const styles = useStyles();
  const [mode, setMode] = useState<Mode>('choose');
  const [targetCompanyId, setTargetCompanyId] = useState<string | null>(null);
  const [targetCompanyName, setTargetCompanyName] = useState<string>('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data: contacts = [], isLoading } = useQuery({
    queryKey: ['company-contacts', companyId],
    queryFn: () => fetchCustomerContacts(companyId),
    enabled: isOpen && !!companyId,
    staleTime: 60 * 1000,
  });

  const reset = () => {
    setMode('choose');
    setTargetCompanyId(null);
    setTargetCompanyName('');
    setBusy(false);
    setError(null);
  };

  const handleClose = () => {
    if (busy) return;
    reset();
    onClose();
  };

  const handleReassignAndDelete = async () => {
    if (!targetCompanyId) {
      setError('Pick a target company first.');
      return;
    }
    if (targetCompanyId === companyId) {
      setError('Pick a different company.');
      return;
    }
    setError(null);
    setBusy(true);
    try {
      // Reassign sequentially — most companies have a handful of contacts,
      // and serial requests give us a clean failure point if one breaks.
      for (const contact of contacts) {
        await setCustomerCompany(contact.id, targetCompanyId);
      }
      onMutated?.();
      // Now retry the delete (no cascade needed — contacts are no longer linked).
      await deleteCustomer(companyId);
      onResolved();
      reset();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Reassignment failed.');
      setBusy(false);
    }
  };

  const handleCascadeDelete = async () => {
    setError(null);
    setBusy(true);
    try {
      await deleteCustomer(companyId, { cascade: true });
      onResolved();
      reset();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Cascade delete failed.');
      setBusy(false);
    }
  };

  const renderChoose = () => {
    if (isLoading) {
      return (
        <div className={styles.body}>
          <Spinner size="small" label="Checking for linked contacts…" />
        </div>
      );
    }
    // No contacts → render a plain confirmation. We're already past the row
    // menu, so this IS the confirmation prompt; no separate dialog stacks
    // in front of it.
    if (contacts.length === 0) {
      return (
        <div className={styles.body}>
          <div>
            Delete <strong>{companyName}</strong>? This cannot be undone.
          </div>
          {error && <div className={styles.errorBanner}>{error}</div>}
        </div>
      );
    }
    return (
      <div className={styles.body}>
        <div className={styles.warningRow}>
          <Warning24Filled className={styles.warningIcon} />
          <div>
            <strong>{companyName}</strong> has {contacts.length} active{' '}
            {contacts.length === 1 ? 'contact' : 'contacts'} linked to it.
          </div>
        </div>
        <div className={styles.contactList}>
          {contacts.map((c: Customer) => (
            <div key={c.id} className={styles.contactRow}>
              <span className={styles.contactName}>{c.name}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Plain delete (no contacts to handle). Used by the 0-contacts confirmation.
  const handlePlainDelete = async () => {
    setError(null);
    setBusy(true);
    try {
      await deleteCustomer(companyId);
      onResolved();
      reset();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed.');
      setBusy(false);
    }
  };

  const renderReassign = () => (
    <div className={styles.body}>
      <div>
        Move all {contacts.length}{' '}
        {contacts.length === 1 ? 'contact' : 'contacts'} to a different company,
        then delete <strong>{companyName}</strong>.
      </div>
      <div>
        <div className={styles.pickerLabel}>New company for these contacts</div>
        <CompanyPicker
          selectedId={targetCompanyId}
          selectedName={targetCompanyName}
          onChange={(id, name) => {
            setTargetCompanyId(id);
            setTargetCompanyName(name);
            setError(null);
          }}
          disabled={busy}
          error={!!error && !targetCompanyId}
        />
      </div>
      {error && <div className={styles.errorBanner}>{error}</div>}
    </div>
  );

  const renderCascadeConfirm = () => (
    <div className={styles.body}>
      <div className={styles.warningRow}>
        <Warning24Filled className={styles.warningIcon} />
        <div>
          This will permanently archive{' '}
          <strong>
            {contacts.length + 1}{' '}
            {contacts.length + 1 === 1 ? 'customer' : 'customers'}
          </strong>{' '}
          (the company <strong>{companyName}</strong> plus all{' '}
          {contacts.length} of its contacts) in Booqable and TWM. Existing
          orders and invoices stay intact, but these records will no longer
          appear in customer pickers.
        </div>
      </div>
      <div className={styles.dangerCascade}>
        Are you sure? This cannot be undone.
      </div>
      {error && <div className={styles.errorBanner}>{error}</div>}
    </div>
  );

  let title: string;
  let actions: React.ReactNode;
  let body: React.ReactNode;

  if (mode === 'choose') {
    title = `Delete ${companyName}?`;
    body = renderChoose();
    if (isLoading) {
      // While we don't yet know whether contacts exist, only Cancel is safe.
      actions = (
        <Button appearance="secondary" onClick={handleClose} disabled={busy}>
          Cancel
        </Button>
      );
    } else if (contacts.length === 0) {
      // Plain confirmation — no contacts to deal with.
      actions = (
        <>
          <Button appearance="secondary" onClick={handleClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            appearance="primary"
            style={{
              backgroundColor: tokens.colorPaletteRedBackground3,
              borderColor: tokens.colorPaletteRedBorderActive,
            }}
            onClick={handlePlainDelete}
            disabled={busy}
          >
            {busy ? 'Working…' : 'Delete'}
          </Button>
        </>
      );
    } else {
      actions = (
        <>
          <Button appearance="secondary" onClick={handleClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            appearance="secondary"
            onClick={() => setMode('cascadeConfirm')}
            disabled={busy}
          >
            Delete company AND contacts
          </Button>
          <Button
            appearance="primary"
            onClick={() => setMode('reassign')}
            disabled={busy}
          >
            Reassign contacts…
          </Button>
        </>
      );
    }
  } else if (mode === 'reassign') {
    title = `Reassign contacts of ${companyName}`;
    body = renderReassign();
    actions = (
      <>
        <Button
          appearance="secondary"
          onClick={() => {
            setMode('choose');
            setError(null);
          }}
          disabled={busy}
        >
          Back
        </Button>
        <Button
          appearance="primary"
          onClick={handleReassignAndDelete}
          disabled={busy || !targetCompanyId}
        >
          {busy ? 'Working…' : 'Reassign and delete'}
        </Button>
      </>
    );
  } else {
    title = `Delete ${companyName} and all contacts?`;
    body = renderCascadeConfirm();
    actions = (
      <>
        <Button
          appearance="secondary"
          onClick={() => {
            setMode('choose');
            setError(null);
          }}
          disabled={busy}
        >
          Back
        </Button>
        <Button
          appearance="primary"
          style={{
            backgroundColor: tokens.colorPaletteRedBackground3,
            borderColor: tokens.colorPaletteRedBorderActive,
          }}
          onClick={handleCascadeDelete}
          disabled={busy}
        >
          {busy ? 'Working…' : `Delete ${contacts.length + 1} customers`}
        </Button>
      </>
    );
  }

  return (
    <BaseDialog
      isOpen={isOpen}
      onClose={handleClose}
      title={title}
      maxWidth="540px"
      showCloseButton={!busy}
      actions={actions}
    >
      {body}
    </BaseDialog>
  );
};

export default CompanyDeleteConflictModal;
